import { createContext, useContext, useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';

import {
  SETTLE,
  settleDelay,
  settleMode,
  settleStyle,
  settleVariants,
  type SettleKind,
  type SettleMode,
} from '@/lib/motion';
import { cn } from '@/lib/utils';
// PROTOTYPE(show-open): dev-only, every use is behind import.meta.env.DEV (dropped from prod).
import { protoBeats, protoReplaySuffix, type ProtoBeats } from '@/prototypes/show-open/beats';

/*
 * The load settle (ui-ux.md §4 "Load settle") — the ONE implementation.
 * Wrap LOADED content, never a skeleton: the settle plays when the element
 * mounts with its data. Never on scroll; never replayed on a re-render or
 * refetch (variants only play on mount); and a group plays once per history
 * entry, so Back/Forward to a page (or re-opening a season) renders at rest.
 * Transforms/opacity only; content is in the DOM and in the accessibility
 * tree from the first frame, and input is never blocked.
 */

interface SettleContext {
  mode: SettleMode;
  /** When this group's `order={0}` is scheduled (performance.now() ms). */
  start: number;
  /** PROTOTYPE(show-open): dev-only beat override; `nested` = a section group under it. */
  proto?: { beats: ProtoBeats; nested: boolean };
}

const Ctx = createContext<SettleContext | null>(null);

/** `${location.key}:${group id}` of every group that has played; bounded. */
const played = new Set<string>();
const MAX_PLAYED = 300;

function remember(key: string) {
  played.add(key);
  if (played.size > MAX_PLAYED) played.delete(played.values().next().value as string);
}

export interface RevealGroupProps {
  /** Names the sequence for the replay guard — unique on the page. */
  id: string;
  /**
   * Place within the enclosing group's cascade (× `SETTLE.stagger`). A
   * section whose data is already there continues the cascade; one whose
   * data lands after its slot starts its own beats from that moment (a late
   * mount never waits on the schedule, and keeps its internal order).
   */
  order?: number;
  children: ReactNode;
}

/** One orchestrated sequence: the mode, the replay guard and the time base. Renders no DOM. */
export function RevealGroup({ id, order = 0, children }: RevealGroupProps) {
  const parent = useContext(Ctx);
  const { key } = useLocation();
  const playedKey = `${key}:${id}${import.meta.env.DEV ? protoReplaySuffix() : ''}`;
  const [ctx] = useState<SettleContext>(() => {
    const now = performance.now();
    // PROTOTYPE(show-open): a root group may take a dev-only beat override; sections inherit it.
    const beats = import.meta.env.DEV
      ? parent
        ? parent.proto?.beats
        : protoBeats(id)
      : undefined;
    return {
      mode: played.has(playedKey) ? 'none' : settleMode(),
      start: Math.max(now, (parent?.start ?? now) + order * SETTLE.stagger * 1000),
      proto: beats ? { beats, nested: parent !== null } : undefined,
    };
  });
  useEffect(() => remember(playedKey), [playedKey]);
  return <Ctx.Provider value={ctx}>{children}</Ctx.Provider>;
}

/** Seconds from now until the group's slot (0 outside a group or once it has passed). */
function slot(group: SettleContext | null): number {
  return group ? Math.max(0, group.start - performance.now()) / 1000 : 0;
}

type RevealTag = 'div' | 'li' | 'section' | 'aside' | 'header';

export interface RevealProps {
  /**
   * The beat (rich desktop only; everywhere else every kind is the same
   * ≤150ms fade): `rise` (default) — copy lines, headings, sections;
   * `stage` — a backdrop; `case` — a hero `PerspectiveCover`; `railItem` —
   * one cover of a rail (put the rail's item classes on the Reveal itself);
   * `row` — one row of a long list (the rise on a 45ms step).
   */
  kind?: SettleKind;
  /**
   * Step in the group's cascade: × `SETTLE.stagger` (110ms), past
   * `SETTLE.maxOrder` renders at rest. For `railItem` it is the item's index:
   * × 90ms, capped at `SETTLE.rail.cap` (later items share the last delay).
   * For `row` it is the row's index: × 45ms, past `SETTLE.row.cap` at rest.
   */
  order?: number;
  as?: RevealTag;
  className?: string;
  children?: ReactNode;
}

/** One settling element. Outside a `RevealGroup` it settles on its own mount. */
export function Reveal({ kind = 'rise', order = 0, as = 'div', className, children }: RevealProps) {
  const group = useContext(Ctx);
  // Fixed at mount: the element type never swaps under its children.
  const [motionProps] = useState(() => {
    const mode = group?.mode ?? settleMode();
    const delay = settleDelay(kind, order);
    if (mode === 'none' || delay === null) return null;
    if (import.meta.env.DEV && group?.proto) {
      // PROTOTYPE(show-open): `undefined` keeps the production beat, `null` is at rest.
      const { beats, nested } = group.proto;
      const beat = (nested ? beats.sections : beats.hero)(kind, order, slot(group));
      if (beat !== undefined) return beat;
    }
    return {
      variants: settleVariants(mode, slot(group) + delay, kind),
      style: mode === 'rich' ? settleStyle(kind) : undefined,
    };
  });
  if (!motionProps) {
    const Tag = as;
    return <Tag className={className}>{children}</Tag>;
  }
  const MotionTag = motion[as];
  return (
    <MotionTag
      variants={motionProps.variants}
      style={motionProps.style}
      initial="initial"
      animate="animate"
      className={className}
    >
      {children}
    </MotionTag>
  );
}

export interface RevealFillProps {
  /** Step in the group's cascade (× `SETTLE.stagger`) — after the copy has landed. */
  order?: number;
  className?: string;
  /** Contains the progress bar(s): any `[role=progressbar]` whose child is the fill. */
  children: ReactNode;
}

/**
 * The fill beat: on rich desktop, each progress bar inside grows its fill
 * from the left (`scaleX` 0 → 1, `SETTLE.fillDuration`) at its slot. A plain
 * div everywhere else, and for a group that already played. It does not
 * settle itself — put it inside the line's `Reveal`.
 */
export function RevealFill({ order = 0, className, children }: RevealFillProps) {
  const group = useContext(Ctx);
  const [style] = useState(() => {
    const mode = group?.mode ?? settleMode();
    const delay = settleDelay('rise', order);
    if (mode !== 'rich' || delay === null) return null;
    if (import.meta.env.DEV && group?.proto && !group.proto.beats.fill) return null; // PROTOTYPE(show-open)
    return {
      '--settle-fill-delay': `${Math.round((slot(group) + delay) * 1000)}ms`,
      '--settle-fill-duration': `${SETTLE.fillDuration * 1000}ms`,
    } as CSSProperties;
  });
  return (
    <div className={cn(style && 'settle-fill', className)} style={style ?? undefined}>
      {children}
    </div>
  );
}
